import { AdjustmentMode, BoundOperator, type QualityFormulaType, type SpecType } from '@local/lib';
import { Button, Checkbox, Input, Radio, RadioButtonGroup, Tooltip } from '@salesforce/design-system-react';
import { useState } from 'react';

import type { RangeFormulaRow } from '../qualityFormulaTypes';

import { IconCategory } from '../../../shared/types/Enum';
import { toNumberOrDefault } from '../numberInputValue';
import { ADJUSTMENT_MODES, AMOUNT_CONFIG_BY_FORMULA_TYPE, FIELD_HELP } from '../qualityFormulaMetadata';
import { getRangeRowExpressionLabel, validateRangeRow } from '../qualityFormulaValidation';
import { AnimatedItem } from './AnimatedItem';
import { FieldHelpTooltip } from './FieldHelpTooltip';
import './rangeRowStyles.css';

interface RangeRowsTableProps {
  formulaType: QualityFormulaType.Percentage | QualityFormulaType.PerUnit;
  onRemoveRow: (specType: SpecType, rowIndex: number) => void;
  onRowChange: (
    specType: SpecType,
    rowIndex: number,
    field: keyof RangeFormulaRow,
    value: boolean | null | number | string,
  ) => void;
  rows: RangeFormulaRow[];
  specType: SpecType;
}

const LOWER_OPERATOR_OPTIONS = [
  { label: '>', value: BoundOperator.Exclusive },
  { label: '≥', value: BoundOperator.Inclusive },
];
const UPPER_OPERATOR_OPTIONS = [
  { label: '<', value: BoundOperator.Exclusive },
  { label: '≤', value: BoundOperator.Inclusive },
];

const NUMBER_INPUT_STYLE: React.CSSProperties = { width: 76 };

// Compact "does the boundary itself count?" toggle — real legacy formulas mix inclusive and
// exclusive per row (see getRangeBoundaryIssues), so it's exposed here rather than assumed fixed.
// Built on `RadioButtonGroup`/`Radio` (`variant="button-group"`) — a true single-select radio
// group is the semantically correct widget for "choose exactly one of these two operators."
//
// `onChange` is wired on the *group*, not each `Radio` — `RadioGroup` clones every `Radio` child
// with its own `onChange` prop (to inject the shared `name`), silently overwriting whatever
// `onChange` an individual `Radio` was given. Reading the selected value back off
// `event.target.value` (always a string; `BoundOperator`'s members are themselves string enum
// values) is therefore the only `onChange` that actually fires.
const BoundOperatorControl: React.FC<{
  data: Array<{ label: string; value: BoundOperator }>;
  disabled?: boolean;
  groupName: string;
  label: string;
  onChange: (value: BoundOperator) => void;
  value: BoundOperator;
}> = ({ data, disabled, groupName, label, onChange, value }) => (
  <Tooltip align="top" content="Does this bound include the boundary value itself?">
    <RadioButtonGroup
      assistiveText={{ label }}
      className="qfw-range-operator"
      disabled={disabled}
      name={groupName}
      onChange={event => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-assignment -- see ProductPriceBasisSection.tsx
        onChange(event.target.value as BoundOperator);
      }}
    >
      {data.map(option => (
        <Radio
          checked={value === option.value}
          key={option.value}
          labels={{ label: option.label }}
          value={option.value}
          variant="button-group"
        />
      ))}
    </RadioButtonGroup>
  </Tooltip>
);

interface RangeRowProps {
  amountConfig: (typeof AMOUNT_CONFIG_BY_FORMULA_TYPE)[keyof typeof AMOUNT_CONFIG_BY_FORMULA_TYPE];
  errors: ReturnType<typeof validateRangeRow>;
  isLastRow: boolean;
  onRemove: () => void;
  onRowChange: (field: keyof RangeFormulaRow, value: boolean | null | number | string) => void;
  removable: boolean;
  row: RangeFormulaRow;
  rowIndex: number;
  style: React.CSSProperties;
}

// One Range row, rendered as a real `<tr>` — split out purely to keep RangeRowsTable's own render
// focused on the table shell (thead + row list) rather than every cell's own markup.
const RangeRow: React.FC<RangeRowProps> = ({
  amountConfig,
  errors,
  isLastRow,
  onRemove,
  onRowChange,
  removable,
  row,
  rowIndex,
  style,
}) => (
  <tr style={style}>
    {/* The Discount/Premium color cue lives on this first cell's own left border, not the `<tr>`
        — `slds-table_bordered`'s `border-collapse` computes borders at the cell level, so a
        `border-left` set on the row itself never actually paints. */}
    <td className={row.adjustmentMode === AdjustmentMode.Discount ? 'qfw-range-discount' : 'qfw-range-premium'}>
      <Tooltip align="right" content={getRangeRowExpressionLabel(row)}>
        <span className="slds-text-body_small slds-text-color_weak" style={{ cursor: 'help', fontWeight: 700 }}>
          {rowIndex + 1}
        </span>
      </Tooltip>
    </td>

    <td>
      <RadioButtonGroup
        assistiveText={{ label: 'Adjustment' }}
        className="qfw-range-adjustment"
        name={`adjustment-mode-${row.id}`}
        onChange={event => {
          onRowChange('adjustmentMode', event.target.value);
        }}
      >
        {ADJUSTMENT_MODES.map(mode => (
          <Radio
            checked={row.adjustmentMode === mode.value}
            className={mode.value === AdjustmentMode.Discount ? 'qfw-range-discount' : 'qfw-range-premium'}
            key={mode.value}
            labels={{ label: mode.label }}
            value={mode.value}
            variant="button-group"
          />
        ))}
      </RadioButtonGroup>
    </td>

    <td>
      <div
        className="slds-grid slds-grid_vertical-align-center slds-nowrap"
        style={{ gap: 'var(--slds-g-spacing-2, 0.5rem)' }}
      >
        <BoundOperatorControl
          data={LOWER_OPERATOR_OPTIONS}
          groupName={`lower-bound-operator-${row.id}`}
          label="Lower bound operator"
          onChange={value => {
            onRowChange('lowerBoundOperator', value);
          }}
          value={row.lowerBoundOperator}
        />
        <Input
          assistiveText={{ label: 'Lower bound' }}
          onChange={(_event, data) => {
            onRowChange('lowerBound', toNumberOrDefault(data.value, 0));
          }}
          styleInput={NUMBER_INPUT_STYLE}
          type="number"
          value={row.lowerBound}
        />
      </div>
    </td>

    <td>
      <div
        className="slds-grid slds-grid_vertical-align-center slds-nowrap"
        style={{ gap: 'var(--slds-g-spacing-2, 0.5rem)' }}
      >
        <Input
          assistiveText={{ label: 'Upper bound' }}
          disabled={row.upperBound === null}
          errorText={row.upperBound !== null && errors.upperBound !== undefined ? errors.upperBound : undefined}
          onChange={(_event, data) => {
            onRowChange('upperBound', toNumberOrDefault(data.value, 0));
          }}
          placeholder={row.upperBound === null ? 'No upper limit' : undefined}
          styleInput={NUMBER_INPUT_STYLE}
          type="number"
          value={row.upperBound ?? ''}
        />
        <BoundOperatorControl
          data={UPPER_OPERATOR_OPTIONS}
          disabled={row.upperBound === null}
          groupName={`upper-bound-operator-${row.id}`}
          label="Upper bound operator"
          onChange={value => {
            onRowChange('upperBoundOperator', value);
          }}
          value={row.upperBoundOperator}
        />
        {isLastRow ? (
          <Tooltip align="top" content={row.upperBound === null ? 'Has no ceiling' : 'Set no upper limit'}>
            <Button
              assistiveText={{ icon: 'Toggle no upper limit' }}
              className={row.upperBound === null ? 'slds-is-selected' : undefined}
              onClick={() => {
                onRowChange('upperBound', row.upperBound === null ? 0 : null);
              }}
              variant="icon"
            >
              <span aria-hidden="true" style={{ fontSize: 'var(--slds-g-font-scale-1)', lineHeight: 1 }}>
                ∞
              </span>
            </Button>
          </Tooltip>
        ) : null}
      </div>
    </td>

    <td>
      <Input
        assistiveText={{ label: amountConfig.label }}
        errorText={errors.amount}
        onChange={(_event, data) => {
          onRowChange(amountConfig.field, toNumberOrDefault(data.value, 0));
        }}
        styleInput={{ width: 92 }}
        type="number"
        value={row[amountConfig.field]}
      />
    </td>

    <td>
      <Input
        assistiveText={{ label: 'Per increment' }}
        disabled={row.isFlatAdjustment}
        errorText={!row.isFlatAdjustment ? errors.perIncrement : undefined}
        onChange={(_event, data) => {
          onRowChange('perIncrement', toNumberOrDefault(data.value, 0));
        }}
        placeholder={row.isFlatAdjustment ? 'N/A' : undefined}
        styleInput={NUMBER_INPUT_STYLE}
        type="number"
        value={row.isFlatAdjustment ? '' : row.perIncrement}
      />
    </td>

    <td className="slds-text-align_center">
      <Checkbox
        assistiveText={{ label: 'Flat adjustment' }}
        checked={row.isFlatAdjustment}
        onChange={(_event, data) => {
          onRowChange('isFlatAdjustment', data.checked);
        }}
      />
    </td>

    <td>
      {removable ? (
        <Button
          assistiveText={{ icon: `Remove range ${rowIndex + 1}` }}
          iconCategory={IconCategory.Utility}
          iconName="delete"
          onClick={onRemove}
          variant="icon"
        />
      ) : null}
    </td>
  </tr>
);

// Renders every Range row (PERCENTAGE / PER_UNIT formula types, RANGE tier mode) as one compact
// table — one row per range, columns for every configurable field — instead of a stacked card
// per range. Every bound, and its inclusive/exclusive operator, is independently editable on every
// row — a new row's lower bound only *defaults* to the previous row's upper bound (see
// handleAddRow) as a starting point, it's never locked to it; getRangeBoundaryIssues surfaces any
// resulting gap or overlap instead of preventing the edit.
export const RangeRowsTable: React.FC<RangeRowsTableProps> = ({
  formulaType,
  onRemoveRow,
  onRowChange,
  rows,
  specType,
}) => {
  const amountConfig = AMOUNT_CONFIG_BY_FORMULA_TYPE[formulaType];
  const allRowsFlat = rows.every(row => row.isFlatAdjustment);
  const someRowsFlat = rows.some(row => row.isFlatAdjustment);
  // Rows pending their exit animation — still present in `rows` (nothing's removed from the store
  // yet) but faded via AnimatedItem's `visible` prop until the transition finishes and
  // `finalizeRemoveRow` actually calls `onRemoveRow`.
  const [removingRowIds, setRemovingRowIds] = useState<Set<string>>(() => new Set());

  const handleToggleAllFlatAdjustment = (checked: boolean) => {
    rows.forEach((_row, rowIndex) => {
      onRowChange(specType, rowIndex, 'isFlatAdjustment', checked);
    });
  };

  const handleRemoveRow = (rowId: string) => {
    setRemovingRowIds(previous => new Set(previous).add(rowId));
  };

  // Re-looks up the row's current index (rather than capturing it at click time) so an in-flight
  // removal still targets the right row even if another row above it finished removing first.
  const finalizeRemoveRow = (rowId: string) => {
    const currentIndex = rows.findIndex(row => row.id === rowId);
    if (currentIndex !== -1) onRemoveRow(specType, currentIndex);
    setRemovingRowIds(previous => {
      const next = new Set(previous);
      next.delete(rowId);
      return next;
    });
  };

  return (
    <div className="slds-scrollable_x">
      <table
        className="qfw-range-table slds-table slds-table_bordered slds-table_cell-buffer"
        style={{ minWidth: 'max-content' }}
      >
        <thead>
          <tr className="slds-line-height_reset">
            <th scope="col" style={{ width: 28 }} />
            <th scope="col">
              <div className="slds-truncate">Adjustment</div>
            </th>
            <th scope="col">
              <div
                className="slds-grid slds-grid_vertical-align-center slds-truncate"
                style={{ gap: 'var(--slds-g-spacing-1, 0.25rem)' }}
              >
                Lower bound
                <FieldHelpTooltip label="The floor of this range. Defaults to the previous range's upper bound when added, but can be changed." />
              </div>
            </th>
            <th scope="col">
              <div className="slds-truncate">Upper bound</div>
            </th>
            <th scope="col">
              <div className="slds-truncate">{amountConfig.label}</div>
            </th>
            <th scope="col">
              <div className="slds-truncate">Per increment</div>
            </th>
            <th scope="col">
              <div
                className="slds-grid slds-grid_vertical-align-center slds-truncate"
                style={{ gap: 'var(--slds-g-spacing-1, 0.25rem)' }}
              >
                Flat adjustment
                <Checkbox
                  assistiveText={{ label: 'Toggle flat adjustment for all rows' }}
                  checked={allRowsFlat}
                  indeterminate={someRowsFlat && !allRowsFlat}
                  onChange={(_event, data) => {
                    handleToggleAllFlatAdjustment(data.checked);
                  }}
                />
                <FieldHelpTooltip label={FIELD_HELP.flatAdjustment} />
              </div>
            </th>
            {/* Fixed width, reserved whether or not any row can currently show its remove button
                (only rows.length > 2 can) — otherwise the column collapses to ~0 at 1-2 rows and
                every other column visibly shifts left the moment a 3rd row makes it appear. */}
            <th scope="col" style={{ width: 36 }} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => {
            const isLastRow = rowIndex === rows.length - 1;
            const errors = validateRangeRow(row, formulaType);

            return (
              <AnimatedItem
                key={row.id}
                onExited={() => {
                  finalizeRemoveRow(row.id);
                }}
                visible={!removingRowIds.has(row.id)}
              >
                {styles => (
                  <RangeRow
                    amountConfig={amountConfig}
                    errors={errors}
                    isLastRow={isLastRow}
                    onRemove={() => {
                      handleRemoveRow(row.id);
                    }}
                    onRowChange={(field, value) => {
                      onRowChange(specType, rowIndex, field, value);
                    }}
                    removable={rows.length > 2}
                    row={row}
                    rowIndex={rowIndex}
                    style={styles}
                  />
                )}
              </AnimatedItem>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
